import React from 'react';
import { formatEnglishMonthDay } from '@/common/utils/formatEnglishDateTime';
import {
  Text, Box, HStack, Icon, Badge, BadgeText,
} from '@gluestack-ui/themed';
import {
  Check, Calendar, Lock, Trophy,
} from 'lucide-react-native';
import { useEventTaskSlots } from '../hooks/useEventTaskSlots';
import { useTenantEventKinds } from '../../../common/context/TenantSettingsStore';
import { taskRequiresEvaluation } from '../../../common/utils/taskEvaluationUtils';
import { isEventDeliveredTask } from '../../../common/utils/tasks/isEventDeliveredTask';

const getTaskCompletionStatus = (task) => task.submission?.state === 'marked_as_done';

// Icon displayed at the end (completion status) - larger for mobile
const TaskEndIcon = ({ task }) => {
  const isCompleted = getTaskCompletionStatus(task);

  if (!isCompleted) return null;

  return <Icon as={Check} size="md" color="$statusSuccess" testID="completion-check" />;
};

// Icon displayed inline with the title (contextual display) - larger for mobile
const TaskInlineIcon = ({ task, currentBooking }) => {
  const { eventKinds } = useTenantEventKinds();
  const isEventTask = isEventDeliveredTask(task);
  const evaluationRequired = taskRequiresEvaluation(task, eventKinds);
  // Tasks with full_evaluation grading system (transfer tasks, oral exams) contribute to the module grade.
  const isGraded = evaluationRequired && task.gradingSystem === 'full_evaluation';
  const isLocked = task.eventConfig?.locked;
  const isCompleted = getTaskCompletionStatus(task);

  // Priority 1: Lock for locked events
  if (isEventTask && isLocked) {
    return <Icon as={Lock} size="sm" color="$textDisabled" testID="event-locked" />;
  }

  // Priority 2: Date pill for booked events (pending leftovers display like confirmed)
  if (isEventTask && currentBooking) {
    if (currentBooking.status === 'pending' || currentBooking.status === 'confirmed' || currentBooking.status === 'completed') {
      const dateStr = formatEnglishMonthDay(currentBooking.startTime);

      return (
        <Badge size="md" bg="$bgSubtle" testID="event-date-pill">
          <BadgeText size="sm" color="$textSubtle">
            {dateStr}
          </BadgeText>
        </Badge>
      );
    }

    return <Icon as={Calendar} size="sm" color="$textSubtle" testID="event-calendar" />;
  }

  // Priority 3: Calendar for open events
  if (isEventTask) {
    return <Icon as={Calendar} size="sm" color="$textSubtle" testID="event-calendar" />;
  }

  // Priority 4: Trophy for tasks that contribute to the module grade
  if (isGraded) {
    return (
      <Icon
        as={Trophy}
        size="sm"
        color={isCompleted ? '$textDisabled' : '$textSubtle'}
        testID="transfer-task"
      />
    );
  }

  return null;
};

/**
 * TaskItemMobile
 * --------------
 * Mobile-optimized task item component designed for use within cards.
 * Features larger text, better spacing, and consistent navigation behavior.
 * All tasks navigate to TaskScreen when clicked.
 */
const TaskItemMobile = ({
  task, module, selectedModule, tasks, ...props
}) => {
  const courseId = selectedModule?.courseId || module?.courses?.[0]?.id;
  const moduleId = module?.id;
  const moduleVersion = module?.version;
  const { currentBooking } = useEventTaskSlots(courseId, moduleId, moduleVersion, task.id, {
    enabled: isEventDeliveredTask(task) && !task.eventConfig?.locked,
    pollSlots: false,
    pollBookings: false,
    task,
  });

  const isCompleted = getTaskCompletionStatus(task);

  return (
    <Box m="$4" testID={`task-item-mobile-${task.id}`} {...props}>
      <HStack alignItems="center" justifyContent="space-between" space="md">
        <HStack alignItems="center" flex={1} space="sm">
          <Text
            size="md"
            numberOfLines={2}
            color={isCompleted ? '$textSubtle' : '$textHeading'}
            opacity={isCompleted ? 0.7 : 1}
            fontWeight={isCompleted ? '$normal' : '$medium'}
            flex={1}
          >
            {task.title}
          </Text>

          <TaskInlineIcon task={task} currentBooking={currentBooking} />
        </HStack>

        <Box w={24} alignItems="center">
          <TaskEndIcon task={task} />
        </Box>
      </HStack>
    </Box>
  );
};

export default TaskItemMobile;
